export const SULFATE_LOGIC = {
  description: "Sulfates, Chromates, Molybdates & Tungstates Identification Rules",
  
  coreRules: `
=== 硫酸盐、铬酸盐、与钨钼酸盐鉴定法则 (Sulfates, Chromates, Molybdates & Tungstates Protocol) ===

[7.1 硫酸盐的基础霸主 (The Core Sulfates: Ca, Ba, Sr)]
*   **重晶石 (Barite)**: 极重（对于非金属矿物而言密度极高）。寻找**板状晶体 (Tabular crystals)** 往往聚集成“沙漠玫瑰”状或者厚重的透明/白色/浅黄色刀片状晶簇。断面上常有极好的直角和微斜角解理相交。
*   **天青石 (Celestine)**: 与重晶石极度相似，但必须寻找其独一无二的**“天蓝色到浅灰蓝色 (Light blue/Celadon blue)”**色调。如果是带有淡蓝色的板状/柱状透明晶簇，直接关联天青石。
*   **石膏家族 (Gypsum Group)**:
    *   **石膏 / 透石膏 (Selenite)**: 极软（指甲能轻易划刻）。如果呈现出完美透明、能清晰透视过去平行四边形板状晶体，或者有明显的“燕尾双晶”，判定为透石膏。
    *   **雪花石膏 (Alabaster)**: 呈现致密的、几乎像汉白玉一样的细粒纯白色块状。
    *   **硬石膏 (Anhydrite)**: 常常呈白、灰粒状致密块体。它也可能表现出三组直角解理（破裂成立方状碎块），与方解石的菱面体解理不同。体积比普通石膏更致密、硬度更高。
*   **盐湖双雄：钙芒硝 (Glauberite) 与 无水芒硝 (Thenardite)**:
    *   **钙芒硝**: 常呈现**矛头状或板状的晶体 (Spear-shaped or tabular crystals)**，并且极易风化，表面往往有一层白色的粉末状外壳。
    *   **无水芒硝**: 吸水极强，通常呈现白色的双锥状或厚板状块体，常处于干燥环境中。
*   **杂卤石 (Polyhalite)**: 通常呈现出因为含有极其微量氧化铁而产生的**特征性砖红色、肉红色到浅桔色的致密纤维状至块状集合体 (Brick-red to flesh-pink fibrous/massive)**。

[7.2 极度绚丽的有毒次生矿与重金属盐 (The Bright Secondary Minerals)]
*   **铬铅矿 (Crocoite)**: 铬酸盐的绝对王者，不可能认错的颜色。寻找**极度鲜艳的亮红色至红橙色，如同一根根尖锐的火柴棍或长针状晶簇 (Bright reddish-orange acicular/prismatic crystals)**。
*   **钼铅矿 (Wulfenite)**: 颜值担当。寻找**极其平整的、黄橙色/红橙色的方形薄板状晶体 (Square, tabular yellow-orange crystals)**。就像一块块半透明的方形橙色/黄色玻璃片切在石头上。
*   **钨铅矿 (Stolzite)**: 与钼铅矿同晶型，但颜色往往偏向于带有树脂光泽的黄褐色、橙褐色甚至灰黄色。
*   **蓝铜矾 / 胆矾 (Chalcanthite)**: 拥有**无法直视的、极度鲜艳的亮蓝色/电蓝色 (Electric blue/Vivid Blue)**。通常是水溶性的次生结晶，如同大块的蓝色冰糖。
*   **青铅矿 (Linarite)**: 寻找**同样是深邃极致的孔雀蓝色/海蓝色 (Deep azure blue)**，但它不像蓝铜矾大块，它通常以极其细小的针状、长板状晶簇或辐射状附着在其他岩石（常与铅铜矿床伴生）表面，并且绝不溶于水。
*   **特殊的绿铜组合**:
    *   **块铜矾 (Antlerite)**: 暗绿色到翠绿色的短柱状、厚板状结晶，在干旱铜氧化带常见。
    *   **云母铜矿 / 叶硫砷铜石 (Chalcophyllite)**: 极其华丽的铜砷矿物。只要看到**“极度闪亮的翠绿色/海绿色，呈六边形、如同云母一样层层叠叠的极薄鳞片状晶体”**，那是云母铜矿绝对特征。
    *   **针绿矾 (Botryogen 等类群)**: 通常呈现鲜艳的橙红色、暗红或黄褐色的针状/放射状晶体结簇，因富含铁/镁的硫酸盐在风化带极易形成复杂且颜色怪异的毛发状混叠物。

[7.3 钨与钼的重金属核心圈 (The Heavy Molybdates & Tungstates)]
*   **黑钨矿 (Wolframite)**: 钨的最重要矿石。极重。看上去就像是**黑褐色到暗黑色的厚重板条 (Dark grey/black tabular/bladed heavy crystals)**，具有单向极完美的解理，闪耀着半金属至树脂光泽。常在石英脉中。
*   **钨锰矿 (Hübnerite)**: 黑钨矿的“红色”端元变种。如果那种重金属板状晶体呈现出**明显的红褐色、且常常呈半透明的紧密长柱状或放射状束**，那就是钨锰矿。
*   **白钨矿 (Scheelite)**: 常为浅黄色、白色至棕褐色。寻找**像两个金字塔底对底平拼起来的双锥体 (Bipyramidal pseudo-octahedral crystals)**。油脂光泽极强。
*   **铁钼华 / 水钼铁矿 (Ferrimolybdite)**: 辉钼矿的次生风化剥落物。总是呈现为**淡黄色、硫磺色、金黄色的粉末状、土状甚至极细的放射状纤维皮壳 (Pale yellow earthy or fine fibrous crusts/coatings)**，通常像黄色锈斑一样包裹着岩石。

[7.4 蚀变带的复杂硫酸盐]
*   **明矾石 (Alunite)**: 热液强酸性蚀变的产物。通常呈白、灰白甚至带有微红色的致密块状或土状。虽然也会有细小的菱面体晶体，但这块石头舔起来如果有明显的涩酸味，并且处于火成岩风化带，优先指向明矾石。
`,

  diagnosticTests: `
- 面对板状晶体：如果是天蓝色，是天青石；如果是纯白/浅黄/极重，是重晶石；如果是极透明并且极软（指甲刻动），是透石膏。
- 看到呈现大面积粉红色/肉红色的致密块状盐类，首先考虑杂卤石 (Polyhalite)。
- 如果一块薄板状的晶体呈现极其规整的“正方形”，而且是极亮的橙黄色，那是钼铅矿的硬锚点。
- 如果结晶如同火柴棍般尖锐且有极度鲜亮的红/橙红色，没有第二种可能，绝对是铬铅矿。
- 在浅黄色矿物中寻找双锥体（金字塔对接），找到了就能锁定白钨矿。
- 评估黑色/褐色的重板条状石头，解理面上闪烁金属到树脂光泽的，一律判定为黑钨矿系列 (若是明显偏红褐色则是钨锰矿)。
- 判断蓝色的铜矿：如像大块带棱角的工业亮蓝冰糖，是蓝铜矾；如果紧密如蓝铜矿，呈现针状晶簇的海蓝色，是青铅矿。
`
};
